import * as React from "react";
import { Box } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import type { GalleryVariant, ProductGalleryImage } from "./types";
import {
  MAIN_IMAGE_HEIGHT_MODAL,
  MAIN_IMAGE_HEIGHT_PAGE,
  THUMB_SIZE_MODAL,
  THUMB_SIZE_PAGE,
} from "./constants";
import { resolveGallerySrc } from "./galleryUtils";

export interface ProductImageGalleryProps {
  variant: GalleryVariant;
  images: ProductGalleryImage[];
  indexImg: number;
  onChangeIndex: (index: number) => void;
  getAlt: (index: number) => string;
  arrowIconSize: number;
  /** Chỉ dùng khi variant === "page": bấm vào ảnh chính để mở zoom */
  onRequestZoom?: () => void;
}

export const ProductImageGallery = ({
  variant,
  images,
  indexImg,
  onChangeIndex,
  getAlt,
  arrowIconSize,
  onRequestZoom,
}: ProductImageGalleryProps) => {
  const isModal = variant === "modal";
  const thumbSize = isModal ? THUMB_SIZE_MODAL : THUMB_SIZE_PAGE;
  const mainHeights = isModal ? MAIN_IMAGE_HEIGHT_MODAL : MAIN_IMAGE_HEIGHT_PAGE;
  const total = images.length;
  const lastIndex = Math.max(0, total - 1);

  const handleCarouselChange = React.useCallback(
    (nextIndex: number) => onChangeIndex(nextIndex),
    [onChangeIndex],
  );

  return (
    <div
      className="active-image-detail"
      style={isModal ? { width: "100%" } : undefined}>
      <Box
        className={`image-product${isModal ? " product-zoom-gallery" : ""}`}
        sx={{
          alignItems: isModal ? "stretch" : "center",
          width: isModal
            ? { xs: "100%", sm: "100%", md: "100%" }
            : { md: "100%", sm: "75%", xs: "93%" },
        }}>
        <Carousel
          showThumbs={false}
          showStatus={false}
          swipeable={!isModal}
          emulateTouch={!isModal}
          selectedItem={indexImg}
          onChange={handleCarouselChange}
          onClickItem={
            isModal || !onRequestZoom ? () => {} : () => onRequestZoom()
          }
          renderArrowPrev={() => (
            <KeyboardArrowLeftIcon
              className={`btn__left ${indexImg === 0 ? "disabled" : ""}`}
              onClick={() => onChangeIndex(indexImg - 1)}
              sx={{
                fontSize: arrowIconSize,
                display: { sm: "block", xs: isModal ? "block" : "none" },
              }}
            />
          )}
          renderArrowNext={() => (
            <ChevronRightIcon
              className={`btn__right ${indexImg >= lastIndex ? "disabled" : ""}`}
              onClick={() => onChangeIndex(indexImg + 1)}
              sx={{
                fontSize: arrowIconSize,
                display: { sm: "block", xs: isModal ? "block" : "none" },
              }}
            />
          )}>
          {images.map((item, index) => (
            <Box
              key={item.image_path || `slide-${index}`}
              component="img"
              src={resolveGallerySrc(item.image_path)}
              alt={getAlt(index)}
              sx={{
                height: mainHeights,
                width: "100%",
                backgroundColor: isModal ? "#f5f3f2" : "#edeae9",
                objectFit: "contain",
                cursor: isModal ? "default" : "pointer",
                borderRadius: isModal ? 1 : 0,
                ...(isModal
                  ? {
                      WebkitTouchCallout: "default",
                      WebkitUserSelect: "auto",
                      userSelect: "auto",
                    }
                  : {}),
              }}
            />
          ))}
        </Carousel>

        <Box
          component="nav"
          aria-label="Product thumbnails"
          sx={{
            marginTop: isModal ? "8px" : "10px",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: isModal ? "flex-start" : undefined,
            alignSelf: isModal ? "stretch" : undefined,
            width: isModal ? "100%" : undefined,
            gap: isModal ? "6px" : undefined,
          }}>
          {images.map((item, index) => (
            <Box
              key={`thumb-${item.image_path || index}`}
              component="button"
              type="button"
              aria-label={`Show image ${index + 1} of ${total}`}
              aria-current={indexImg === index ? "true" : undefined}
              sx={{
                width: thumbSize,
                height: thumbSize,
                p: 0,
                border: "none",
                cursor: "pointer",
                borderRadius: "6px",
                marginLeft: isModal ? 0 : "5px",
                boxSizing: "border-box",
                background: "transparent",
                display: "block",
                lineHeight: 0,
                ...(indexImg === index
                  ? { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` }
                  : {}),
              }}
              onClick={() => onChangeIndex(index)}>
              <Box
                component="img"
                src={resolveGallerySrc(item.image_path)}
                alt=""
                className={indexImg === index ? "img_choiced" : undefined}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "6px",
                  display: "block",
                  pointerEvents: "none",
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </div>
  );
};
